import * as ort from 'onnxruntime-web';
import { getDownloadURL, ref } from 'firebase/storage';
import type { FrameMetrics, FrameState, Landmark } from '@erectus/shared';
import { firebase } from '@/lib/firebase.js';
import { appConfig } from '@/lib/remoteConfig.js';

/**
 * On-device artifact detector (docs/DATA_AND_ML.md §5, PROMPTS Phase 8 §2).
 *
 * Runs once a second over the last 10 seconds of features and answers one
 * question: "was this really posture, or was the user reaching for a cup?".
 * Frames it calls an artifact are marked invalid rather than counted as a
 * slouch — the single biggest source of false positives in beta.
 *
 * Rollout is gated by Remote Config: `classifier_model_version` names the model
 * and `classifier_rollout_pct` decides what share of users get it. Disagreements
 * with the rule engine are counted on the session document so a bad version is
 * visible before it reaches everyone.
 */
export interface ClassifierMeta {
  version: string;
  labels: string[];
  featureNames: string[];
  windowFrames: number;
  normalizer: { mean: number[]; std: number[] };
  inferenceIntervalMs: number;
}

export interface ClassifierVerdict {
  label: string;
  confidence: number;
  isArtifact: boolean;
}

// WASM binaries are self-hosted next to the MediaPipe ones; no CDN at runtime.
ort.env.wasm.wasmPaths = '/wasm/';

export class ArtifactClassifier {
  private constructor(
    private readonly session: ort.InferenceSession,
    readonly meta: ClassifierMeta,
  ) {}

  /** Load the version named by Remote Config, or null when none is rolled out. */
  static async load(uid: string): Promise<ArtifactClassifier | null> {
    const config = appConfig();
    const version = config.classifierModelVersion;
    if (!version) return null;
    if (!inRollout(uid, config.classifierRolloutPct)) return null;

    try {
      const storage = firebase().storage;
      const [modelUrl, metaUrl] = await Promise.all([
        getDownloadURL(ref(storage, `models/${version}/model.onnx`)),
        getDownloadURL(ref(storage, `models/${version}/meta.json`)),
      ]);
      const meta = (await (await fetch(metaUrl)).json()) as ClassifierMeta;
      const session = await ort.InferenceSession.create(modelUrl, {
        executionProviders: ['webgpu', 'wasm'],
        graphOptimizationLevel: 'all',
      });
      return new ArtifactClassifier(session, meta);
    } catch {
      // A missing or broken model must never break a recording session.
      return null;
    }
  }

  async classify(window: FeatureWindow): Promise<ClassifierVerdict | null> {
    const frames = this.meta.windowFrames;
    const features = window.toMatrix(this.meta.featureNames, frames);
    if (!features) return null;

    const { mean, std } = this.meta.normalizer;
    const flat = new Float32Array(frames * this.meta.featureNames.length);
    for (let f = 0; f < frames; f += 1) {
      for (let c = 0; c < this.meta.featureNames.length; c += 1) {
        const value = features[f]![c]!;
        const divisor = std[c] && Math.abs(std[c]!) > 1e-6 ? std[c]! : 1;
        flat[f * this.meta.featureNames.length + c] = (value - (mean[c] ?? 0)) / divisor;
      }
    }

    try {
      const tensor = new ort.Tensor('float32', flat, [1, frames, this.meta.featureNames.length]);
      const outputs = await this.session.run({ window: tensor });
      const logits = Array.from((outputs.logits?.data ?? []) as Float32Array);
      if (logits.length === 0) return null;
      const probabilities = softmax(logits);
      let best = 0;
      for (let i = 1; i < probabilities.length; i += 1) {
        if (probabilities[i]! > probabilities[best]!) best = i;
      }
      const label = this.meta.labels[best] ?? 'normal';
      return { label, confidence: probabilities[best]!, isArtifact: label === 'artifact' };
    } catch {
      return null;
    }
  }

  release(): void {
    void this.session.release?.();
  }
}

/** Rolling feature buffer fed by the recorder. */
export class FeatureWindow {
  private readonly rows: Array<{ t: number; values: Record<string, number> }> = [];

  constructor(private readonly windowMs = 10_000) {}

  push(t: number, metrics: FrameMetrics, landmarks: readonly Landmark[]): void {
    const previous = this.rows.at(-1);
    this.rows.push({
      t,
      values: {
        cva: metrics.cva ?? 0,
        cha: metrics.cha ?? 0,
        trunk_inclination: metrics.trunkInclination ?? 0,
        shoulder_protraction: metrics.shoulderProtraction ?? 0,
        hip_angle: metrics.hipAngle ?? 0,
        ear_visibility: landmarks[7]?.visibility ?? 0,
        hip_visibility: landmarks[23]?.visibility ?? 0,
        motion: previous ? motionOf(metrics, previous.values) : 0,
      },
    });
    const cutoff = t - this.windowMs;
    while (this.rows.length > 0 && this.rows[0]!.t < cutoff) this.rows.shift();
  }

  /** Resample to exactly `frames` rows, or null when the buffer is too short. */
  toMatrix(featureNames: string[], frames: number): number[][] | null {
    if (this.rows.length < Math.ceil(frames / 2)) return null;
    const out: number[][] = [];
    for (let i = 0; i < frames; i += 1) {
      const index = Math.min(
        this.rows.length - 1,
        Math.floor((i / (frames - 1 || 1)) * (this.rows.length - 1)),
      );
      const row = this.rows[index]!;
      out.push(featureNames.map((name) => row.values[name] ?? 0));
    }
    return out;
  }

  clear(): void {
    this.rows.length = 0;
  }
}

function motionOf(metrics: FrameMetrics, previous: Record<string, number>): number {
  const cva = Math.abs((metrics.cva ?? 0) - (previous.cva ?? 0));
  const trunk = Math.abs((metrics.trunkInclination ?? 0) - (previous.trunk_inclination ?? 0));
  return (cva + trunk) / 100;
}

function softmax(values: number[]): number[] {
  const max = Math.max(...values);
  const exps = values.map((v) => Math.exp(v - max));
  const sum = exps.reduce((a, b) => a + b, 0) || 1;
  return exps.map((v) => v / sum);
}

/** Stable per-user bucketing, so a user does not flip in and out of a rollout. */
export function inRollout(uid: string, percentage: number): boolean {
  if (percentage <= 0) return false;
  if (percentage >= 100) return true;
  let hash = 2166136261;
  for (let i = 0; i < uid.length; i += 1) {
    hash ^= uid.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return Math.abs(hash % 100) < percentage;
}

/**
 * Combine the rule engine with the classifier. The classifier can only ever
 * *invalidate* a frame — it never promotes one to good — so a bad model costs
 * coverage, not correctness.
 */
export function applyClassifier(
  ruleState: FrameState,
  verdict: ClassifierVerdict | null,
): { state: FrameState; disagreement: boolean } {
  if (!verdict || !verdict.isArtifact || verdict.confidence < 0.7) {
    return { state: ruleState, disagreement: false };
  }
  return { state: 'invalid', disagreement: ruleState !== 'invalid' };
}
